import * as React from 'react';

import { Button } from '@/ui/button';
import { cn } from '@/utils';

import durationIcon from '../../assets/duration_icon.png';
import frequencyIcon from '../../assets/frequency_icon.png';
import mathleteBanner from '../../assets/mathlete_banner.png';
import totalSessionsIcon from '../../assets/total_sessions_icon.png';
import type { MathleteBatch } from '../../types';

import { BatchDetailsCard } from './batch-details-card';

const courseDescription = [
  {
    icon: totalSessionsIcon,
    label: 'Total Sessions',
    value: '12',
  },
  {
    icon: frequencyIcon,
    label: 'Frequency of sessions',
    value: '3/week',
  },
  {
    icon: durationIcon,
    label: 'Duration of one session',
    value: '55 min',
  },
];

interface MathleteBatchSelectionViewProps {
  batches: MathleteBatch[];
  selectedBatch: MathleteBatch | null;
  isLoading: boolean;
  isBatchesError: boolean;
  isRegistering: boolean;
  enrollButtonRef: React.RefObject<HTMLButtonElement>;
  onBatchClick: (batch: MathleteBatch) => void;
  onEnrollSubmit: () => void;
  isBatchSelected: (batch: MathleteBatch) => boolean;
}

interface BatchesListProps {
  batches: MathleteBatch[];
  isLoading: boolean;
  isBatchesError: boolean;
  onBatchClick: (batch: MathleteBatch) => void;
  isBatchSelected: (batch: MathleteBatch) => boolean;
}

const BatchesList: React.FC<BatchesListProps> = ({
  batches,
  isLoading,
  isBatchesError,
  onBatchClick,
  isBatchSelected,
}) => {
  if (isLoading) {
    return (
      <div className="text-center text-gray-300 py-8">
        <p>Loading available batches...</p>
      </div>
    );
  }

  if (isBatchesError) {
    return (
      <div className="text-center text-red-400 py-8">
        <p>Error loading batches. Please try again later.</p>
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="text-center text-gray-300 py-8">
        <p>No batches available at this time.</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'grid gap-0 w-[90%] ml-0 h-[190px] overflow-y-auto grid-cols-1',
        'md:h-auto md:grid-cols-3'
      )}
    >
      {batches.map(batch => (
        <BatchDetailsCard
          key={batch.batch_id}
          batch={batch}
          onClick={onBatchClick}
          selected={isBatchSelected(batch)}
        />
      ))}
    </div>
  );
};

export const MathleteBatchSelectionView: React.FC<
  MathleteBatchSelectionViewProps
> = ({
  batches,
  selectedBatch,
  isLoading,
  isBatchesError,
  isRegistering,
  enrollButtonRef,
  onBatchClick,
  onEnrollSubmit,
  isBatchSelected,
}) => {
  return (
    <div className="rounded-lg flex flex-col">
      <div
        className={cn(
          'rounded-lg bg-no-repeat bg-[length:100%_100%]',
          'm-2 mb-0 p-[1.4rem]',
          'md:m-0 md:mb-2 md:p-[4.5rem]'
        )}
        style={{
          backgroundImage: `url(${mathleteBanner})`,
        }}
      >
        <p className="text-base text-white">You have been enrolled in</p>
        <h2 className="text-2xl md:text-4xl font-bold text-white mb-1">
          Mathlete 101
        </h2>
        <p className="text-base text-white">
          Please select your preferred batch
        </p>
      </div>

      <div className="flex flex-col justify-center items-center">
        <div
          className={cn(
            'grid grid-cols-3 gap-4',
            'w-[90%] mt-2 mb-2',
            'md:w-[85%] md:mt-4 md:mb-4'
          )}
        >
          {courseDescription.map((desc, index) => (
            <div
              key={index}
              className="flex flex-col justify-center items-center"
            >
              <img src={desc.icon} alt={desc.label} className="mb-2" />
              <p className="text-sm text-center text-gray-300">{desc.label}:</p>
              <p className="text-base font-semibold text-gray-600">
                {desc.value}
              </p>
            </div>
          ))}
        </div>

        <div
          className={cn(
            'flex items-center justify-center text-center',
            'w-[85%]',
            'm-2',
            'md:m-5'
          )}
        >
          <span className="flex-1 border-b border-dashed border-gray-500 mx-2" />
          <span className="font-bold px-3 text-sm">Select preferred batch</span>
          <span className="flex-1 border-b border-dashed border-gray-500 mx-2" />
        </div>

        <BatchesList
          batches={batches}
          isLoading={isLoading}
          isBatchesError={isBatchesError}
          onBatchClick={onBatchClick}
          isBatchSelected={isBatchSelected}
        />

        <Button
          ref={enrollButtonRef}
          className="mt-4"
          onClick={onEnrollSubmit}
          disabled={!selectedBatch || isRegistering}
          isLoading={isRegistering}
          variant="default"
        >
          Enroll now
        </Button>
      </div>
    </div>
  );
};
